<template>
  <div class="max-w-4xl mx-auto p-6">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Deposit</h1>

      <!-- Navigation/Tab Bar -->
      <div class="flex items-center bg-yellow-100 rounded-lg p-2 mb-6 mt-4">
        <v-btn icon small class="mr-3" @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-gray-800 font-medium">Deposit Fiat</span>
      </div>
    </div>

    <!-- BVN Check Modal -->
    <BvnCheckModal
      :open="needBvnCheck"
      :tier2-message="'You need to complete BVN verification to proceed with Tier 2 KYC verification.'"
      @close="closeBvnCheck"
      @verification-success="handleBvnVerificationSuccess"
      @verification-cancelled="handleBvnVerificationCancelled"
    />

    <!-- Liveness Check Modal -->
    <LivenessCheckModal
      :open="needLivenessCheck"
      :tier2-message="'You need to complete Tier 2 KYC verification to deposit fiat into your wallet.'"
      @close="closeLivenessCheck"
      @verification-success="handleVerificationSuccess"
      @verification-cancelled="handleVerificationCancelled"
    />

    <!-- Section Title and Instructions -->
    <div class="mb-8">
      <div class="mt-4 grid lg:grid-cols-2 grid-cols-1">
        <div>
          <h2 class="text-xl font-semibold text-gray-800 mb-2">
            Deposit {{ this.selectedAsset }}
          </h2>
          <p class="text-gray-600">
            Make use of this account details to make deposit
          </p>
          <div></div>
        </div>
        <div>
          <!-- Virtual Account List -->
          <div class="space-y-4">
            <!-- Warning Message -->
            <div
              class="bg-orange-100 border-l-4 border-orange-500 p-4 rounded-lg"
            >
              <div class="flex items-start">
                <v-icon color="orange" class="mr-3 mt-0.5"
                  >mdi-alert-circle</v-icon
                >
                <div class="text-sm text-orange-800">
                  <p class="font-semibold">Important Notice</p>
                  <p class="mt-1">
                    Make sure your deposit from a bank account that matches the
                    name on your KYC. Third-party deposits are not allowed and
                    may result in loss and withholding of funds.
                  </p>
                </div>
              </div>
            </div>

            <!-- Virtual Account Details -->
            <div
              v-for="(account, index) in virtualAccount"
              :key="account._id"
              class="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
              <div class="space-y-4">
                <!-- Account Number -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Account number:</span
                  >
                  <div class="flex items-center space-x-2">
                    <span class="text-lg font-mono text-gray-900">{{
                      account.accountNumber
                    }}</span>
                    <v-btn
                      icon
                      small
                      @click="copy(account.accountNumber)"
                      class="text-gray-500 hover:text-gray-700"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Bank Name -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Bank name:</span
                  >
                  <span class="text-gray-900">{{ account.bankName }}</span>
                </div>

                <!-- Account Name -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Account name:</span
                  >
                  <span class="text-gray-900">{{ account.accountName }}</span>
                </div>

                <!-- Transaction Fee -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Transaction fee:</span
                  >
                  <span class="text-gray-900">{{ depositFeeLabel }}</span>
                </div>

                <!-- Minimum Deposit -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Minimum Deposit:</span
                  >
                  <span class="text-gray-900">{{ minDepositLabel }}</span>
                </div>

                <!-- Maximum daily deposit -->
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"
                    >Maximum daily deposit:</span
                  >
                  <span class="text-gray-900">{{ maxDailyDepositLabel }}</span>
                </div>
              </div>

              <!-- Divider between accounts -->
              <div
                v-if="index < virtualAccount.length - 1"
                class="mt-6 pt-6 border-t border-gray-200"
              >
                <div class="text-center">
                  <span
                    class="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full"
                  >
                    Alternative Account {{ index + 2 }}
                  </span>
                </div>
              </div>
            </div>

            <!-- No accounts message -->
            <div
              v-if="!virtualAccount || virtualAccount.length === 0"
              class="bg-gray-50 rounded-lg p-8 text-center"
            >
              <v-icon size="48" color="gray" class="mb-4">mdi-bank</v-icon>
              <p class="text-gray-600">No virtual accounts available</p>
              <p class="text-sm text-gray-500 mt-1">
                Complete your KYC verification to generate virtual accounts
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BvnCheckModal from '../../../../components/BvnCheckModal.vue'
import LivenessCheckModal from '../../../../components/LivenessCheckModal.vue'
import profile from '../../../../api/users/profile'
import { updateProfile } from '../../../../utils/helpers'
import wallet from '../../../../api/users/wallet'

export default {
  components: {
    BvnCheckModal,
    LivenessCheckModal
  },
  data() {
    return {
      selectedAsset: '',
      needLivenessCheck: true,
      needBvnCheck: true,
      virtualAccount: []
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      siteData: (state) => state.user.siteSettings
    }),
    currencySymbol() {
      return this.siteData?.currency_sym || '₦'
    },
    depositFeeLabel() {
      const fee = Number(this.siteData?.depositFee || 0)
      const cap = Number(this.siteData?.depositFeeCap || 0)
      if (!(fee > 0)) return 'Free'
      if (this.siteData?.depositFeeType === 'fixed') return this.money(fee)
      return `${fee}%${cap > 0 ? ` (Max ${this.money(cap)})` : ''}`
    },
    minDepositLabel() {
      return this.money(1000)
    },
    maxDailyDepositLabel() {
      return this.money(5000000)
    }
  },
  mounted() {
    // Naira virtual-account funding is Nigeria-only. The asset modal hides the
    // fiat tab elsewhere, but this URL is reachable directly, so turn non
    // Nigerian users away here too rather than letting them start a KYC flow
    // that can never end in a usable account.
    if (this.userCountry !== 'Nigeria') {
      this.$store.dispatch('alert', {
        message: 'Fiat deposit is only available to users in Nigeria',
        error: true,
        status: true
      })
      this.$router.replace('/user/wallet')
      return
    }
    this.$store.dispatch('user/getSiteSettings')
    this.getUserProfile()
    this.selectedAsset = this.$route.params.symbol
    this.checkKYCStatus()
  },
  watch: {
    user: {
      handler() {
        this.checkKYCStatus()
      },
      deep: true
    }
  },
  methods: {
    money(amount) {
      return `${this.currencySymbol} ${Number(amount).toLocaleString()}`
    },
    checkKYCStatus() {
      // BVN and liveness are Nigerian identity checks - never ask Ghana or
      // Kenya users for them. The page redirects them on mount, but the `user`
      // watcher can fire before that unmounts.
      if (this.userCountry !== 'Nigeria') {
        this.needBvnCheck = false
        this.needLivenessCheck = false
        return
      }

      // Check BVN status first
      const bvnKYCCheck = this.user.kycLevels?.find(
        (level) => level.action === 'bvn'
      )
      if (!bvnKYCCheck || bvnKYCCheck.status !== 'completed') {
        this.needBvnCheck = true
        this.needLivenessCheck = false // Don't show liveness check if BVN is not done
        return
      } else {
        this.needBvnCheck = false
      }

      // If BVN is approved, check liveness check status
      const livenessCheckKYCLevel = this.user.kycLevels?.find(
        (level) => level.action === 'liveness_check'
      )
      if (
        !livenessCheckKYCLevel ||
        livenessCheckKYCLevel.status !== 'completed'
      ) {
        this.needLivenessCheck = true
      } else {
        this.needLivenessCheck = false
        this.generateVirtualAccount()
      }
    },
    async generateVirtualAccount() {
      const req = await wallet.data().generateVirtualAccount()
      if (req.error) {
        this.$store.dispatch('alert', {
          message: req.errorMessage?.message || 'An error occurred',
          error: true,
          status: true
        })
        this.loadingAddress = false
        return
      }

      this.virtualAccount = req.data.data
    },
    copy(address) {
      navigator.clipboard.writeText(address)
      this.$store.dispatch('alert', {
        message: 'Address copied to clipboard',
        error: false,
        status: true
      })
    },

    closeBvnCheck() {
      this.needBvnCheck = false
    },

    handleBvnVerificationSuccess(data) {
      console.log('BVN verification successful:', data)
      this.needBvnCheck = false
      // Refresh user profile to get updated KYC levels
      this.getUserProfile().then(() => {
        // After BVN is verified, check if liveness check is needed
        this.checkKYCStatus()
      })
    },

    handleBvnVerificationCancelled() {
      console.log('BVN verification cancelled')
      this.needBvnCheck = false
    },

    closeLivenessCheck() {
      this.needLivenessCheck = false
    },

    handleVerificationSuccess(data) {
      console.log('Liveness verification successful:', data)
      this.needLivenessCheck = false
      this.getUserProfile()
      this.generateVirtualAccount()
    },

    handleVerificationCancelled() {
      console.log('Liveness verification cancelled')
      this.needLivenessCheck = false
    },
    async getUserProfile() {
      const res = await profile.profile().get(['bankAccounts'])
      updateProfile({ user: 'user', profile: res.data.data })
      return res
    }
  }
}
</script>

<style scoped>
/* Custom styles for the purple button */
.v-btn--color-purple {
  background-color: #8b5cf6 !important;
  color: white !important;
}

.v-btn--color-purple:hover {
  background-color: #7c3aed !important;
}

.v-btn--color-purple:disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
}
</style>
